import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { Divider, Tooltip, Alert } from 'antd';

import {
  Form,
  FormItem,
  Radio,
  Input,
  Checkbox,
  DatePicker,
  Select,
} from 'formik-antd';

import { InfoCircleOutlined, UserOutlined } from '@ant-design/icons';

import {
  simpleFilterOptions,
  getUrlFromEvent,
  DEFAULT_BIRTH_YEAR,
  over18,
} from 'utils/form';

import { dateFormat } from 'utils/dates';
import nationalities from 'utils/nationalities';
import { Fieldset } from 'components/form';
import { SingleFileUpload } from 'components/form/upload';
import RightToWork from 'components/right-to-work';
import StagePropTypes from './prop-types';
import { DB_PREFIX, DB_PREFIX_INFO, DB_PREFIX_REGISTER } from './constants';

const helpDOB = 'To compile with regulations you have to be over 18 years old';

const description = (
  <div>
    <p>
      We need this information to enable the potential employer to check that
      you are safe to work with their patients, service users or residents.
    </p>
    <p>
      This information will only be shared with a potential employer if they
      connect with you.
    </p>
  </div>
);

const warning = (
  <div>
    <p>
      If your DBS certificate is less than 12 months old we can use this for
      your initial registration. We will use the one you have already uploaded
      as part of the registration process and share with potential employers.
    </p>

    <p>
      If it is more than 12 months old then you will need to apply for new one.
      This can be done by clicking through the following link{' '}
      <a href="mailto:dbs@my-shifts.co.uk?subject=New DBS application request">
        dbs@my-shifts.co.uk
      </a>
    </p>

    <p>You need to state your full name, D.O.B and email address.</p>

    <p>
      You will need to pay for this using the instructions supplied by Optima
      HSC Ltd. The current fee is £55:00. *If you complete 20 shifts via
      MyShifts we will give you a credit for training courses to the same value.
    </p>

    <p>
      Once this payment is received we will ask our DBS partner DDC to contact
      you directly by email using the email address you have supplied as part of
      the registration process and you can complete the application form online
      via them.
    </p>

    <p>
      There will be instructions and guidance from them on completing the form
      fully and ensuring you have all the information needed to make an
      application. Without full information they will not be able to submit the
      application, as it will be rejected by DBS.
    </p>

    <p>
      Best of luck and we look forward to completing your registration very
      soon.
    </p>
  </div>
);

const DBSForm = ({
  isSubmitting,
  values,
  setFieldValue,
  uploadTo,
  children,
}) => {
  const {
    [DB_PREFIX]: { fileURL },
  } = values || {};

  const file = fileURL
    ? {
        name: 'Certificate',
        url: fileURL,
      }
    : undefined;

  const { registered } = values[DB_PREFIX_REGISTER];

  return (
    <Form layout="vertical">
      <Alert description={description} type="info" showIcon />
      <Divider />
      <FormItem
        label="Are you registered with the online DBS update service?"
        name={`${DB_PREFIX_REGISTER}.registered`}
      >
        <Radio.Group name={`${DB_PREFIX_REGISTER}.registered`} defaultValue="a">
          <Radio.Button value>Yes</Radio.Button>
          <Radio.Button value={false}>No</Radio.Button>
        </Radio.Group>
      </FormItem>

      {registered === true ? (
        <>
          <Fieldset>
            <FormItem label="First Name" name={`${DB_PREFIX_INFO}.firstName`}>
              <Input
                name={`${DB_PREFIX_INFO}.firstName`}
                placeholder="Name"
                prefix={<UserOutlined />}
              />
            </FormItem>
            <FormItem label="Last Name" name={`${DB_PREFIX_INFO}.lastName`}>
              <Input
                name={`${DB_PREFIX_INFO}.lastName`}
                placeholder="Last name"
                prefix={<UserOutlined />}
              />
            </FormItem>
          </Fieldset>

          <Fieldset>
            <FormItem
              name={`${DB_PREFIX_INFO}.dateOfBirth`}
              label={
                <span>
                  Date Of Birth
                  <Tooltip title={helpDOB}>
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              }
            >
              <DatePicker
                style={{ width: '100%' }}
                name={`${DB_PREFIX_INFO}.dateOfBirth`}
                format={dateFormat}
                disabledDate={over18}
                defaultPickerValue={moment()
                  .clone()
                  .set('year', DEFAULT_BIRTH_YEAR)}
              />
            </FormItem>

            <FormItem label="Certificate No" name={`${DB_PREFIX}.id`}>
              <Input name={`${DB_PREFIX}.id`} placeholder="00000" />
            </FormItem>
          </Fieldset>

          <FormItem name={`${DB_PREFIX_INFO}.nationality`} label="Nationality">
            {values[DB_PREFIX_INFO].nationality !== 'British' &&
            values[DB_PREFIX_INFO].nationality != null ? (
              <RightToWork />
            ) : null}

            <Select
              name={`${DB_PREFIX_INFO}.nationality`}
              placeholder="Please select your nationality"
              showSearch
              filterOption={simpleFilterOptions}
              allowClear
            >
              {nationalities.map((value, i) => (
                <Select.Option key={`${value}-${i}`} value={value}>
                  {value}
                </Select.Option>
              ))}
            </Select>
          </FormItem>

          <Divider />

          <Fieldset>
            <FormItem name={`${DB_PREFIX}.permissionToCheckDBS`}>
              <Checkbox name={`${DB_PREFIX}.permissionToCheckDBS`}>
                Allow My Shifts to do a DBS check
              </Checkbox>
            </FormItem>
          </Fieldset>

          <FormItem
            name={`${DB_PREFIX}.fileURL`}
            label="Official Document"
            help="Please upload a copy now (it must be the full document and include all information recorded on it)"
          >
            <SingleFileUpload
              className="ant-upload ant-upload-drag"
              file={file}
              uploadTo={uploadTo}
              multiple={false}
              showUploadList
              disabled={file != null || isSubmitting}
              subTitle="Click in this area to upload your last paper DBS Certificate"
              onClear={() => {
                setFieldValue(`${DB_PREFIX}.fileURL`, null);
              }}
              onChange={(e) => {
                setFieldValue(`${DB_PREFIX}.fileURL`, getUrlFromEvent(e));
              }}
            />
          </FormItem>
        </>
      ) : null}

      {registered === false ? (
        <Alert description={warning} type="warning" showIcon />
      ) : null}

      {children}
    </Form>
  );
};

DBSForm.propTypes = {
  uploadTo: PropTypes.string.isRequired,
  isSubmitting: PropTypes.bool,
  setFieldValue: PropTypes.func.isRequired,
  values: StagePropTypes.isRequired,
  children: PropTypes.node,
};

DBSForm.defaultProps = {
  isSubmitting: false,
  children: null,
};

export default DBSForm;
